/**
 * @module ErrorBoundaryBridge
 * @package @geenius/adapters/react-native
 * @description Native error boundary bridge for adapter-driven UI surfaces.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import { Pressable, StyleSheet, Text, View } from "../lib/native";
import { semanticColors } from "./TokenBridge";

/**
 * Props for the native adapter error boundary bridge.
 *
 * @property children React subtree protected by the error boundary.
 * @property fallback Optional custom fallback node or render callback.
 * @property onError Optional callback invoked with the thrown error and info.
 * @property style Optional native style merged onto the default fallback.
 * @property testID Optional deterministic selector for tests and Storybook.
 */
export interface ErrorBoundaryBridgeProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  onError?: (error: Error, info: ErrorInfo) => void;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/** Internal state tracked by {@link ErrorBoundaryBridge}. */
interface ErrorBoundaryBridgeState {
  error: Error | null;
}

/**
 * Catches render errors inside native adapter surfaces.
 *
 * @param props Error boundary props including children and optional fallback.
 * @returns The child subtree, custom fallback, or default native error panel.
 * @example
 * ```tsx
 * <ErrorBoundaryBridge onError={(error) => logger.error(error.message)}>
 *   <AdaptersPage />
 * </ErrorBoundaryBridge>
 * ```
 */
export class ErrorBoundaryBridge extends Component<
  ErrorBoundaryBridgeProps,
  ErrorBoundaryBridgeState
> {
  state: ErrorBoundaryBridgeState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryBridgeState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;

    if (!error) {
      return this.props.children;
    }

    if (typeof this.props.fallback === "function") {
      return this.props.fallback(error, this.reset);
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <View
        accessibilityRole="alert"
        style={[styles.container, this.props.style]}
        testID={this.props.testID ?? "adapter-error-boundary"}
      >
        <Text style={styles.title}>Adapter error</Text>
        <Text style={styles.message}>{error.message}</Text>
        <Pressable
          accessibilityLabel="Reset adapter error"
          accessibilityRole="button"
          onPress={this.reset}
          style={styles.button}
          testID="adapter-error-reset"
        >
          <Text style={styles.buttonText}>Retry</Text>
        </Pressable>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  button: {
    alignSelf: "flex-start",
    backgroundColor: semanticColors.status.error.text,
    borderRadius: 8,
    marginTop: 12,
    paddingHorizontal: 14,
    paddingVertical: 9,
  },
  buttonText: {
    color: semanticColors.brand.onBrand,
    fontSize: 13,
    fontWeight: "800",
  },
  container: {
    backgroundColor: semanticColors.status.error.bg,
    borderColor: semanticColors.status.error.border,
    borderRadius: 10,
    borderWidth: 1,
    padding: 14,
  },
  message: {
    color: semanticColors.status.error.textStrong,
    fontSize: 13,
    marginTop: 4,
  },
  title: {
    color: semanticColors.status.error.text,
    fontSize: 15,
    fontWeight: "900",
  },
});
